import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import HomeIcon from '@mui/icons-material/Home';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SecurityIcon from '@mui/icons-material/Security';
import { Box, Divider, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';

const drawerWidth = 280;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `64px`,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  flexShrink: 0,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const navItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
  { text: 'Admin Overview', icon: <AdminPanelSettingsIcon />, path: '/admin-overview' },
  { text: 'Guest & Host Management', icon: <GroupIcon />, path: '/admin/guest-host-management' },
  { text: 'Botel Management', icon: <HomeIcon />, path: '/admin/botel-management' },
  { text: 'Waitlist Management', icon: <ListAltIcon />, path: '/admin/waitlist-management' },
  { text: 'Financial Management', icon: <AccountBalanceIcon />, path: '/financial-management' },
  { text: 'Security & Compliance', icon: <SecurityIcon />, path: '/security-compliance' },
  { text: 'Maintenance & Operations', icon: <BuildIcon />, path: '/maintenance-operations' },
  { text: 'System Monitoring & Analytics', icon: <AssessmentIcon />, path: '/system-monitoring-analytics' },
];

const AdminSideDrawer = ({ open }: { open: boolean }) => {
  const theme = useTheme();
  const location = useLocation();
  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader>
        {open && (
          <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
             <img src="/logo.png" alt="BellHop Logo" width={100} />
          </Box>
        )}
      </DrawerHeader>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)', // Subtract the header height
          overflow: 'hidden',
        }}
      >
        <List 
          sx={{ 
            mt: 1,
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem
                key={item.text}
                component={Link}
                to={item.path}
                sx={{
                  justifyContent: open ? 'flex-start' : 'center',
                  px: open ? 2 : 1,
                  my: 0.5,
                  borderRadius: 2,
                  background: isActive ? '#3456D5' : 'transparent',
                  color: isActive ? '#fff' : '#b0b8c1',
                  '&:hover': {
                    background: isActive ? '#3456D5' : 'rgba(52,86,213,0.08)',
                    color: '#fff',
                  },
                  minHeight: 48,
                  transition: 'background 0.2s',
                  borderLeft: isActive ? '6px solid #3456D5' : '6px solid transparent',
                  flexWrap: 'wrap',
                  wordBreak: 'break-word',
                }}
              >
                <ListItemIcon sx={{ color: isActive ? '#fff' : '#b0b8c1', minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                {open && (
                  <ListItemText 
                    primary={item.text} 
                    sx={{
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word',
                      '& .MuiListItemText-primary': {
                        fontSize: '0.875rem',
                        lineHeight: 1.2,
                      }
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};

export default AdminSideDrawer; 